import { ROUTE_DASHBOARD } from '@/constants/routes';
import { useSetFooterIsVisible, useSetIntercomIsVisible } from '@/queries/layout.queries';
import { useGetRiskProcessingStatus } from '@/queries/risk-processing-status.queries';
import { RiskProcessingStatus } from '@/repositories/risk-processing-status.repository';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

let interval: NodeJS.Timeout | null = null;
const REDIRECT_URL_KEY = 'redirect-url';

export default function useLoadingScreenController() {
  const navigate = useNavigate();

  const { refetch } = useGetRiskProcessingStatus();
  const [status, setStatus] = useState<RiskProcessingStatus>(RiskProcessingStatus.processing);
  const [stateInterval, setStateInterval] = useState<NodeJS.Timeout | null>(null);
  const [isCalculationComplete, setIsCalculationComplete] = useState(false);
  const mutateIntercomIsVisible = useSetIntercomIsVisible();
  const mutateFooterIsVisible = useSetFooterIsVisible();
  const { url, firstAssessment } = useParams();
  const isFirstAssessment = firstAssessment === 'true' ? true : false;
  const redirectUrl = decodeURIComponent(url ?? '');

  const storeInterval = (intervalToStore: NodeJS.Timeout) => {
    interval = intervalToStore;
    setStateInterval(intervalToStore);
  };

  useEffect(() => {
    localStorage.setItem(REDIRECT_URL_KEY, redirectUrl);
  }, [redirectUrl]);

  const checkStatus = async () => {
    try {
      const { data: riskProcessingStatusData } = await refetch();
      const processingStatus = riskProcessingStatusData?.riskProcessingStatus;

      if (!processingStatus) return null;

      if (processingStatus !== status) {
        setStatus(processingStatus);
      }

      if (processingStatus === RiskProcessingStatus.processed) {
        setIsCalculationComplete(true);
        if (stateInterval) clearInterval(stateInterval);
        navigate(localStorage.getItem(REDIRECT_URL_KEY) ?? ROUTE_DASHBOARD);
      }
    } catch (err) {
      const error = err as AxiosError;
      console.error(error);
      setStatus(RiskProcessingStatus.error);
    }
  };

  useEffect(() => {
    if (!stateInterval) {
      storeInterval(setInterval(() => checkStatus(), 5 * 1000));
    }

    setTimeout(() => {
      if (stateInterval) clearInterval(stateInterval);
    }, 12 * 60 * 1000);

    return () => {
      clearInterval(interval ?? 0);
    };
  }, []);

  useEffect(() => {
    mutateIntercomIsVisible.mutateAsync(false);
    return () => {
      mutateIntercomIsVisible.mutateAsync(true);
    };
  }, []);

  useEffect(() => {
    mutateFooterIsVisible.mutateAsync(false);
    return () => {
      mutateFooterIsVisible.mutateAsync(true);
    };
  }, []);

  const resetStatus = () => {
    setStatus(RiskProcessingStatus.processing);
  };

  return { status, isFirstAssessment, isCalculationComplete, resetStatus };
}
